import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { SEARCH_MIN_LENGTH, type SearchHit, type SearchKind, type SearchResponse } from "@klantpunt/shared";
import { api } from "../lib/api";
import {
  IconAddressBook,
  IconAssets,
  IconBoard,
  IconBuilding,
  IconEquipment,
  IconHelp,
  IconIssues,
  IconMoon,
  IconPlus,
  IconScan,
  IconSearch,
  IconSun,
  IconUser,
} from "./icons";
import { companyPath } from "../features/directory/parts";
import { buildingPath, type NavGroup } from "./navItems";

/** Opent het opdrachtmenu van overal (knop Zoeken, sneltoets). */
export const COMMAND_MENU_EVENT = "pw-command-menu";
export const openCommandMenu = () => window.dispatchEvent(new Event(COMMAND_MENU_EVENT));

/** "⌘K" op Apple, anders "Ctrl K". */
export const commandShortcut = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? "⌘K"
    : "Ctrl K";

type Icon = ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" }>;

interface Command {
  id: string;
  group: string;
  label: string;
  detail?: string | null;
  code?: string | null;
  Icon: Icon;
  /** Extra zoekwoorden voor acties ("melding", "nieuw"). */
  keywords?: string;
  run: () => void;
  /** Bewaren bij Recent (zoekresultaten). */
  hit?: SearchHit;
}

const GROUP_OF: Record<SearchKind, string> = {
  building: "Gebouwen",
  issue: "Bevindingen",
  task: "Taken",
  asset: "Assets",
  company: "Adresboek",
  person: "Adresboek",
};
const ICON_OF: Record<SearchKind, Icon> = {
  building: IconBuilding,
  issue: IconIssues,
  task: IconBoard,
  asset: IconAssets,
  company: IconAddressBook,
  person: IconUser,
};

/** Adres van een zoekresultaat; zelfde links als vanuit Mijn taken, planning en adresboek. */
export function hitPath(h: SearchHit): string {
  const b = h.buildingId ?? "";
  switch (h.kind) {
    case "building":
      return buildingPath(h.id, "overzicht");
    case "issue":
      return `${buildingPath(b, "bevindingen")}?bevinding=${h.id}`;
    case "task":
      return `${buildingPath(b, "taken")}?taak=${h.id}`;
    case "asset":
      return `${buildingPath(b, h.assetKind === "equipment" ? "arbeidsmiddelen" : "assets")}?asset=${h.id}`;
    case "company":
      return companyPath(h.id);
    case "person":
      return `/adresboek?persoon=${h.id}`;
  }
}

const RECENT_MAX = 5;
const recentKey = (orgId: string) => `pw-cmdk-recent-${orgId}`;
function readRecent(orgId: string): SearchHit[] {
  try {
    const v = JSON.parse(localStorage.getItem(recentKey(orgId)) ?? "[]") as unknown;
    return Array.isArray(v) ? (v as SearchHit[]).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
function pushRecent(orgId: string, hit: SearchHit) {
  try {
    const next = [hit, ...readRecent(orgId).filter((h) => !(h.kind === hit.kind && h.id === hit.id))];
    localStorage.setItem(recentKey(orgId), JSON.stringify(next.slice(0, RECENT_MAX)));
  } catch {
    /* geen opslag */
  }
}

/** Kleine letters zonder accenten, voor het filteren van acties. */
const norm = (s: string) => s.toLocaleLowerCase("nl").normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Ctrl/Cmd+K overal, "/" als je niet in een veld typt en er geen venster open is. */
export function useCommandMenuShortcut(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openCommandMenu();
        return;
      }
      if (e.key !== "/" || e.ctrlKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      const editing = t?.closest("input, textarea, select, [contenteditable=''], [contenteditable='true']");
      if (editing || document.querySelector(".modal, .cmdk")) return;
      e.preventDefault();
      openCommandMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled]);
}

/**
 * Opdrachtmenu (Ctrl/Cmd+K of "/"): één zoekveld om naar een gebouw, bevinding, taak, asset of contact te
 * springen, naar een onderdeel uit het menu te gaan of een actie te starten. Pijltjes en Enter kiezen; Escape
 * en de terugknop van de telefoon (Android) sluiten het menu.
 */
export function CommandMenu({
  orgId,
  groups,
  canCreate,
  canScan,
  helpTo,
  theme,
  onNewIssue,
  onToggleTheme,
}: {
  orgId: string;
  groups: NavGroup[];
  canCreate: boolean;
  canScan: boolean;
  helpTo: string;
  theme: "light" | "dark";
  onNewIssue: () => void;
  onToggleTheme: () => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(COMMAND_MENU_EVENT, onOpen);
    return () => window.removeEventListener(COMMAND_MENU_EVENT, onOpen);
  }, []);
  if (!open) return null;
  return (
    <CommandDialog
      orgId={orgId}
      groups={groups}
      canCreate={canCreate}
      canScan={canScan}
      helpTo={helpTo}
      theme={theme}
      onNewIssue={onNewIssue}
      onToggleTheme={onToggleTheme}
      onClosed={() => setOpen(false)}
    />
  );
}

function CommandDialog({
  orgId,
  groups,
  canCreate,
  canScan,
  helpTo,
  theme,
  onNewIssue,
  onToggleTheme,
  onClosed,
}: Parameters<typeof CommandMenu>[0] & { onClosed: () => void }) {
  const navigate = useNavigate();
  const titleId = useId();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const pending = useRef<(() => void) | null>(null);
  const pushed = useRef(false);
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 150);
    return () => window.clearTimeout(id);
  }, [q]);

  // Terugknop (Android) sluit het menu: bij openen een stap in de geschiedenis, die bij sluiten weer weg gaat.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    window.history.pushState({ ...(window.history.state ?? {}), cmdk: true }, "");
    pushed.current = true;
    const onPop = () => {
      pushed.current = false;
      const run = pending.current;
      pending.current = null;
      closedRef.current();
      if (run) run();
      else prev?.focus?.();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /** Sluiten en daarna eventueel iets doen (na het terugzetten van de geschiedenis, zodat navigeren klopt). */
  function close(then?: () => void) {
    if (pushed.current) {
      pending.current = then ?? null;
      window.history.back();
    } else {
      closedRef.current();
      then?.();
    }
  }

  const searching = debounced.length >= SEARCH_MIN_LENGTH;
  const results = useQuery({
    queryKey: ["search", orgId, debounced],
    queryFn: () => api<SearchResponse>(`/search?q=${encodeURIComponent(debounced)}`),
    enabled: searching,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const recent = useMemo(() => readRecent(orgId), [orgId]);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => close(() => navigate(to));
    const openHit = (h: SearchHit) => () =>
      close(() => {
        pushRecent(orgId, h);
        navigate(hitPath(h));
      });
    const hitCommand = (h: SearchHit, group: string): Command => ({
      id: `${group}-${h.kind}-${h.id}`,
      group,
      label: h.title,
      code: h.code,
      detail: h.subtitle,
      Icon: h.kind === "asset" && h.assetKind === "equipment" ? IconEquipment : ICON_OF[h.kind],
      run: openHit(h),
      hit: h,
    });

    const actions: Command[] = [
      ...(canCreate
        ? [
            {
              id: "a-new-issue",
              group: "Acties",
              label: "Nieuwe bevinding",
              keywords: "melden melding toevoegen",
              Icon: IconPlus,
              run: () => close(onNewIssue),
            },
          ]
        : []),
      ...(canScan
        ? [
            {
              id: "a-scan",
              group: "Acties",
              label: "QR-code scannen",
              keywords: "scan sticker camera",
              Icon: IconScan,
              run: go("/scan"),
            },
          ]
        : []),
      {
        id: "a-theme",
        group: "Acties",
        label: theme === "dark" ? "Licht thema" : "Donker thema",
        keywords: "thema kleur weergave nacht",
        Icon: theme === "dark" ? IconSun : IconMoon,
        run: () => close(onToggleTheme),
      },
      {
        id: "a-help",
        group: "Acties",
        label: "Hulp en support",
        keywords: "help vraag",
        Icon: IconHelp,
        run: go(helpTo),
      },
    ];
    const nav: Command[] = groups.flatMap((g) =>
      g.items.map((it) => ({
        id: `n-${it.key}`,
        group: "Ga naar",
        label: it.label,
        keywords: it.title,
        Icon: it.Icon,
        run: go(it.to),
      })),
    );

    if (!q.trim()) return [...recent.map((h) => hitCommand(h, "Recent")), ...actions, ...nav];

    const words = norm(q).split(/\s+/).filter(Boolean);
    const matches = (c: Command) => {
      const text = norm(`${c.label} ${c.keywords ?? ""}`);
      return words.every((w) => text.includes(w));
    };
    const hits = searching && results.data?.q === debounced ? results.data.hits : [];
    return [...[...nav, ...actions].filter(matches), ...hits.map((h) => hitCommand(h, GROUP_OF[h.kind]))];
    // close en navigate zijn stabiel genoeg; opnieuw opbouwen bij elke toets is niet nodig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, debounced, results.data, recent, groups, canCreate, canScan, theme, helpTo]);

  useEffect(() => setActive(0), [q, results.data]);
  const current = Math.min(active, Math.max(commands.length - 1, 0));

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${current}"]`)?.scrollIntoView({ block: "nearest" });
  }, [current]);

  function onKeyDown(e: ReactKeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown" || (e.ctrlKey && e.key === "n")) {
      e.preventDefault();
      setActive((current + 1) % Math.max(commands.length, 1));
    } else if (e.key === "ArrowUp" || (e.ctrlKey && e.key === "p")) {
      e.preventDefault();
      setActive((current - 1 + commands.length) % Math.max(commands.length, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commands[current]?.run();
    } else if (e.key === "Tab") {
      // Focus blijft in het zoekveld; de lijst bedien je met de pijltjes.
      e.preventDefault();
    }
  }

  const status = !q.trim()
    ? null
    : searching && results.isError
      ? "Zoeken in gegevens lukt nu niet. Controleer je verbinding."
      : searching && results.isFetching && !commands.length
        ? "Zoeken…"
        : !commands.length
          ? q.trim().length < SEARCH_MIN_LENGTH
            ? "Typ minstens twee tekens."
            : `Niets gevonden voor "${q.trim()}".`
          : null;

  let lastGroup = "";
  return createPortal(
    <div className="modalbg cmdk-bg" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
        <h2 id={titleId} className="sr-only">
          Zoeken en opdrachten
        </h2>
        <div className="cmdk-field">
          <IconSearch size={20} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={commands[current] ? `${listId}-${current}` : undefined}
            aria-label="Zoek een gebouw, bevinding, asset of contact, of typ een opdracht"
            placeholder="Zoek of typ een opdracht"
            enterKeyHint="go"
            autoComplete="off"
            spellCheck={false}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="button" className="btn ghost small cmdk-close" onClick={() => close()}>
            Sluiten
          </button>
        </div>
        <ul className="combolist cmdk-list" id={listId} role="listbox" aria-label="Resultaten" ref={listRef}>
          {commands.map((c, i) => {
            const head = c.group !== lastGroup ? c.group : null;
            lastGroup = c.group;
            return (
              <li key={c.id} role="presentation">
                {head && (
                  <div className="combogroup" role="presentation">
                    {head}
                  </div>
                )}
                <div
                  id={`${listId}-${i}`}
                  role="option"
                  data-index={i}
                  aria-selected={i === current}
                  onMouseMove={() => i !== current && setActive(i)}
                  onClick={c.run}
                >
                  <c.Icon size={18} aria-hidden="true" />
                  <span className="combotext">
                    <span className="combolabel">
                      {c.code && c.code !== c.label && (
                        <>
                          <span className="cmdk-code">{c.code}</span>{" "}
                        </>
                      )}
                      {c.label}
                    </span>
                    {c.detail && <span className="small muted">{c.detail}</span>}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
        {status && (
          <p className="cmdk-status muted small" role="status">
            {status}
          </p>
        )}
        <p className="cmdk-keys small muted" aria-hidden="true">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> kiezen
          </span>
          <span>
            <kbd>Enter</kbd> openen
          </span>
          <span>
            <kbd>Esc</kbd> sluiten
          </span>
        </p>
      </div>
    </div>,
    document.body,
  );
}
